import { useTranslations } from 'use-intl';
import { Explain } from '@/ui/Explain';

export interface LoadDay {
  date: string;
  mdLabel: string | null;
  plannedLoad: number | null;
  actualLoad: number | null;
  isMatch?: boolean;
  isToday?: boolean;
  label?: string | null;
}

/**
 * The week's load profile.
 *
 * This is the product's signature picture: the shape of a training week against
 * the match it prepares for. Planned is drawn as an outline and actual as a
 * solid bar, so the gap between what was intended and what happened is the thing
 * you see first — which is the entire argument for storing both.
 *
 * Deliberately not a charting library: eight bars with two series does not need
 * 40KB, and hand-drawn bars keep tabular figures and RTL mirroring consistent
 * with the rest of the product.
 */
export function LoadProfile({ days }: { days: readonly LoadDay[] }) {
  const t = useTranslations();
  const peak = Math.max(
    1,
    ...days.map((d) => Math.max(d.plannedLoad ?? 0, d.actualLoad ?? 0)),
  );

  const height = (load: number | null) =>
    load === null || load <= 0 ? 0 : Math.max(3, Math.round((load / peak) * 100));

  return (
    <div className="overflow-x-auto px-4 py-4">
      {/* No `min-w` in the ordinary case. A minimum width inside a scroller
          still widens the layout viewport, which put a sideways scroll on the
          dashboard and the week screen. A week is seven or eight columns and
          its labels are short ("MD-2", "19"), so they divide the width they
          have; a longer series keeps the scroller. */}
      <div className={`flex items-end gap-1 sm:gap-2 ${days.length > 10 ? 'min-w-[32rem]' : ''}`}>
        {days.map((d) => {
          const plannedH = height(d.plannedLoad);
          const actualH = height(d.actualLoad);
          return (
            <div key={d.date} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
              <div className="flex h-32 w-full items-end justify-center gap-1">
                {/*
                  Nothing is drawn for nothing.

                  A zero-height box with a dashed border still paints its
                  border — so a rest day showed a 1px dashed squiggle on the
                  baseline, and a week with three of them looked like a chart
                  with dirt on it. An empty day should be empty.
                */}
                {plannedH > 0 ? (
                  /* Planned: an outline, because it is an intention. */
                  <div
                    className="w-1/2 rounded-t-[2px] border border-dashed border-line-strong bg-transparent"
                    style={{ height: `${plannedH}%` }}
                    title={`${t('plan.planned')} ${d.plannedLoad ?? 0}`}
                  />
                ) : null}
                {actualH > 0 ? (
                  /* Actual: solid, because it happened. */
                  <div
                    className={`w-1/2 rounded-t-[2px] ${
                      d.isMatch ? 'bg-load-peak' : 'bg-load-moderate'
                    }`}
                    style={{ height: `${actualH}%` }}
                      title={`${t('plan.actual')} ${d.actualLoad ?? 0}`}
                  />
                ) : null}
              </div>
              {/* A fixture carries no training load, so it needs its own mark —
                  otherwise the day the week is built around is the one day that
                  shows nothing. */}
              <span
                className={`block h-1 w-full rounded-full ${
                  d.isMatch ? 'bg-load-peak' : 'bg-transparent'
                }`}
                title={d.isMatch ? t('calendar.kind_match') : undefined}
              />
              <span
                className={`tabular font-condensed text-meta font-semibold ${
                  d.isMatch ? 'text-load-peak' : d.isToday ? 'text-ink' : 'text-ink-faint'
                }`}
              >
                {d.mdLabel ?? '·'}
              </span>
              <span
                className={`tabular text-[0.6875rem] ${
                  d.isToday ? 'font-semibold text-ink' : 'text-ink-faint'
                }`}
              >
                {Number(d.date.slice(8, 10))}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-ink-faint">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 border border-dashed border-line-strong" />
          {t('plan.planned')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 bg-load-moderate" />
          {t('plan.actual')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-1 w-4 rounded-full bg-load-peak" />
          {t('calendar.kind_match')}
        </span>
        {/* The legend is where the outline-versus-solid distinction is
            explained, so it is where the term belongs. */}
        <Explain term="planned_actual" />
      </p>
    </div>
  );
}
